/**
 * سربرگِ «کرالر ↔ اپ» — یک سؤال و بس: از رویدادهایی که کرالر همین حالا
 * منتشرشدنی می‌داند، چند تا واقعاً در اپ نشسته‌اند و چند تا نه.
 *
 * ═══ چرا سربرگِ جدا و نه بخشی از «خطِ لوله‌ی کرالر» ═══
 *
 * آن یکی می‌گوید خطِ لوله **چه کرد** (چند صفحه گرفت، کجا ریخت). این یکی
 * می‌گوید نتیجه‌اش **الان کجاست**. دو سوالِ متفاوت با دو ریتم: آن یکی را
 * بعدِ هر اجرا نگاه می‌کنی، این یکی را وقتی می‌خواهی بدانی چیزی که ساخته شد
 * به دستِ کاربر رسیده یا نه. و برخلافِ بقیه‌ی این اپ، اعدادش **جفت
 * نمی‌شوند** (مایگریشنِ ۰۱۰۸): این‌جا «الان» است، نه «در ۱۰ روزِ گذشته» یا
 * «در آخرین اجرا».
 *
 * ═══ هیچ منطقی این‌جا نیست ═══
 *
 * تعریفِ «آماده‌ی انتشار» و «هماهنگ» در مخزنِ event_crawler است (مایگریشنِ
 * ۰۰۳۲ آن، دو ویو). مایگریشنِ ۰۰۸۹ی این مخزن فقط از آن‌ها می‌خواند و این
 * فایل فقط نمایششان می‌دهد. هر عددی که این‌جا اشتباه به نظر رسید، جوابش آن
 * ویوهاست نه این کامپوننت.
 *
 * ═══ چرا حالت‌های «سالم» هم نشان داده می‌شوند ═══
 *
 * چون دو تایشان دقیقاً همان‌هایی‌اند که آدم را به اشتباه می‌اندازند:
 *
 *   • «تا شده در سری» ردیفِ خودش را در `events` **ندارد** و این خرابی نیست
 *     — زیرِ والدش یک occurrence است. اگر جدا شمرده نشود، صد و اندی رویدادِ
 *     سالم «گم‌شده» به نظر می‌رسند.
 *   • «یتیم در اپ» معمولاً هم خرابی نیست: یعنی pipeline از آخرین انتشار به
 *     این‌طرف کار کرده و هنوز چیزی به اپ نرفته. برای همین «آخرین انتشار» با
 *     عددِ درشت بالای صفحه است — بدونش این ستون بی‌معنی است.
 */
import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { opsRepo, type PublishSyncGroup, type PublishSyncRow } from '@/core/data/opsRepo';
import { Hint } from '@/ui/Hint';

const fmt = (n: number | null | undefined) =>
  (typeof n === 'number' ? n.toLocaleString('en-US') : '—');

const stamp = (v: string | null) =>
  (v ? new Date(v).toLocaleString('en-CA', { dateStyle: 'short', timeStyle: 'short' }) : '—');

/** ثانیه → «۹ ساعت». واحدِ درشت‌تر از دقت مهم‌تر است؛ این عدد برای تصمیم است نه گزارش. */
const since = (sec: number | null | undefined): string => {
  if (typeof sec !== 'number') return 'هرگز';
  const m = Math.round(sec / 60);
  if (m < 60) return `${m} دقیقه`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h} ساعت`;
  return `${Math.round(h / 24)} روز`;
};

/**
 * هر حالتِ ویو، با اینکه سالم است یا نه و اینکه **یعنی چه**.
 *
 * `ok` فقط رنگ نیست؛ ترتیبِ صفحه را هم می‌سازد: سالم‌ها بالا و جمع‌شده،
 * ناسالم‌ها پایین و بازشده با ردیفِ واقعی. عددی که کاری نمی‌خواهد نباید جای
 * عددی را بگیرد که می‌خواهد.
 */
const STATES: Record<string, { label: string; ok: boolean; why: string; help?: string }> = {
  in_sync: {
    label: 'هماهنگ', ok: true,
    why: 'ردیفِ خودش در اپ منتشر است و عنوان، شروع، پایان و آدرسش با کرالر یکی است.',
  },
  in_sync_as_occurrence: {
    label: 'تا شده در سری', ok: true,
    why: 'ردیفِ جدا ندارد و لازم هم نیست — زیرِ والدِ سری یک occurrence است و کاربر می‌بیندش.',
    help:
      'ردیفِ جدای خودش را در اپ ندارد، ولی گم نشده: یکی از تاریخ‌های یک '
      + 'رویدادِ تکرارشونده است.\n\n'
      + 'مثال: «یوگا در پارک» هر شنبه برگزار می‌شود. کرالر ۱۲ تاریخِ جدا '
      + 'می‌بیند، ولی اپ یک رویدادِ «یوگا در پارک» با ۱۲ تاریخ می‌سازد. آن ۱۲ '
      + 'تا این‌جا «تا شده در سری» شمرده می‌شوند.\n\n'
      + 'کارِ لازم: هیچ. کاربر هر ۱۲ تاریخ را می‌بیند.',
  },
  archived_past: {
    label: 'گذشته، بایگانی', ok: true,
    why: 'رویدادش تمام شده و اپ بایگانی‌اش کرده. کرالر هنوز آماده‌اش می‌داند ولی کاری لازم نیست.',
  },
  admin_decision: {
    label: 'حکمِ ادمین', ok: true,
    why: 'آدمی در صفِ بازبینی ردش کرده یا ادغامش کرده. عمدی است، پس ناهماهنگی نیست.',
  },
  content_drift: {
    label: 'محتوا عوض شده', ok: false,
    why: 'در اپ منتشر است ولی کرالر حالا عنوان یا تاریخِ دیگری دارد. ستونِ «دلیل» می‌گوید کدام فیلد.',
  },
  app_row_missing: {
    label: 'ردیفِ اپ گم شده', ok: false,
    why: 'نگاشتِ انتشار هست ولی ردیفی که به آن اشاره می‌کند در اپ نیست. یعنی چیزی ردیف را پاک کرده.',
    help:
      'یادداشتی داریم که می‌گوید «این رویداد را با فلان شناسه در اپ منتشر '
      + 'کردیم»، ولی سراغِ آن شناسه که می‌رویم ردیفی آن‌جا نیست.\n\n'
      + 'مثال: کرالر «کنسرتِ جاز» را منتشر کرد و شناسه‌اش در اپ شد ۸۸۱. بعد '
      + 'چیزی ردیفِ ۸۸۱ را از جدولِ رویدادها پاک کرد. یادداشتِ انتشار مانده، '
      + 'خودِ رویداد رفته.\n\n'
      + 'کارِ لازم: این واقعاً خرابی است — کاربر رویدادی را که منتشر کرده‌ایم '
      + 'نمی‌بیند. سؤال این است که چه چیزی ردیف‌ها را پاک می‌کند.',
  },
  never_published: {
    label: 'هرگز منتشر نشده', ok: false,
    why: 'آماده است و هیچ‌وقت به اپ نرفته. اگر عددش بزرگ شد، publisher اجرا نشده یا شکسته.',
  },
  orphan_in_app: {
    label: 'یتیم در اپ', ok: false,
    why: 'در اپ منتشر است ولی کرالر امروز منتشرش نمی‌کند. معمولاً یعنی pipeline جلو رفته و انتشار عقب مانده — «آخرین انتشار» بالای صفحه را نگاه کن.',
    help:
      'در اپ منتشر است و کاربر می‌بیندش، ولی اگر امروز از کرالر بپرسی «چه '
      + 'چیزی باید منتشر شود؟» این یکی در فهرستش نیست.\n\n'
      + 'مثال: دیشب «نمایشگاهِ عکس» منتشر شد. امروز کرالر دوباره دوید و آن '
      + 'رویداد دیگر منتشرشدنی شمرده نمی‌شود — مثلاً منبع صفحه‌اش را عوض '
      + 'کرده. ردیفِ دیشبی هنوز در اپ نشسته و حالا «یتیم» است.\n\n'
      + 'کارِ لازم: معمولاً هیچ. این تقریباً همیشه یعنی کرالر جلو رفته و '
      + 'انتشار هنوز نرسیده. اول «آخرین انتشار» بالای صفحه را نگاه کن؛ اگر '
      + 'مالِ چند ساعت پیش است فقط تأخیر است. ستونِ «دلیل» می‌گوید کرالر چرا '
      + 'کنارش گذاشته.',
  },
};

/** حالت‌های `hidden_*` اسمِ ثابتی ندارند: `hidden_` + هر وضعیتی که اپ دارد. */
const stateInfo = (state: string) =>
  STATES[state] ?? (state.startsWith('hidden_')
    ? {
        label: `پنهان در اپ (${state.slice(7)})`, ok: false,
        why: 'ردیفش در اپ هست ولی وضعیتش منتشر نیست، پس هیچ کاربری نمی‌بیندش.',
        help:
          'ردیفش در اپ ساخته شده، ولی وضعیتش «منتشر» نیست — پس هیچ کاربری '
          + 'نمی‌بیندش. داخلِ پرانتز همان وضعیتِ واقعیِ ردیف نوشته می‌شود.\n\n'
          + 'مثال: «پنهان در اپ (pending_review)» یعنی ردیف هست ولی در صفِ '
          + 'بازبینی مانده و منتظرِ تاییدِ توست. «پنهان در اپ (rejected)» یعنی '
          + 'کسی ردش کرده.\n\n'
          + 'کارِ لازم: به وضعیتِ داخلِ پرانتز بستگی دارد — بعضی‌هایشان منتظرِ '
          + 'تو هستند و بعضی‌هایشان تصمیمِ عمدیِ خودت بوده‌اند.',
      }
    : { label: state, ok: false, why: '' });

/** دلیل‌های سمتِ «یتیم» — همان چیزی که publisher برای رد کردنش داشت. */
const REASONS: Record<string, string> = {
  cluster_not_live: 'خوشه‌اش در آخرین نسل نیست',
  no_canonical_row: 'ردیفِ canonical ندارد',
  no_decision: 'هیچ حکمی برایش نیست',
  decision_outdated: 'حکم برای محتوای قدیمی است',
  decision_publish: 'حکمش publish است ولی آماده شمرده نشده',
  decision_review: 'حکمش «بازبینی» است',
  decision_reject: 'حکمش «رد» است',
  folded_into_group: 'در یک گروه تا شده',
};

/**
 * توضیحِ ماوس‌نگه‌داشتنِ دلیل‌ها — همان الگوی `STAGE_HELP`/`TODO_HELP`.
 *
 * ⚠️ جدا از `REASONS` است و نه ادغام‌شده با آن، چون `REASONS` جای دیگری هم
 * به‌عنوانِ متنِ خام خوانده می‌شود؛ و کلیدی که توضیح ندارد باید بی‌سروصدا
 * بی‌توضیح بماند، نه اینکه `undefined` را به `title` بدهد.
 *
 * هر متن سه چیز دارد: **یعنی چه**، **یک مثال**، و اگر کاری لازم است **چه
 * کاری**. مخاطبش کسی است که این واژه‌ها را ننوشته و قرار نیست بنویسد.
 */
const REASON_HELP: Record<string, string> = {
  cluster_not_live:
    'کرالر هر بار که می‌دود یک «نسل» تازه از خوشه‌ها می‌سازد. خوشه‌ی این '
    + 'رویداد در نسلِ آخر نیامده — یعنی در آخرین اجرا دیگر پیدایش نکرده.\n\n'
    + 'مثال: منبع صفحه‌ی تقویمش را عوض کرده و آن رویداد را برداشته. اجرای بعدی '
    + 'دیگر خوشه‌ای برایش نساخت. ردیفِ قبلی هنوز در اپ هست ولی پشتش خالی است.\n\n'
    + 'کارِ لازم: اگر رویداد واقعاً لغو شده، درست است. اگر هنوز روی سایت هست، '
    + 'یعنی خواندنِ آن منبع خراب شده — سربرگِ «منابع» را نگاه کن.',

  no_canonical_row:
    'خوشه هست، ولی هیچ‌کدام از روایت‌هایش به‌عنوانِ «نسخه‌ی رسمی» انتخاب '
    + 'نشده. تا وقتی یکی رسمی نشود، معلوم نیست کدام عنوان و کدام تاریخ باید '
    + 'منتشر شود.\n\n'
    + 'مثال: سه منبع همان کنسرت را گزارش کرده‌اند با سه عنوانِ کمی متفاوت. '
    + 'مرحله‌ی merge هنوز نگفته کدام‌یک عنوانِ نهایی است، پس چیزی برای منتشر '
    + 'کردن وجود ندارد.',

  no_decision:
    'سیاستِ انتشار هنوز درباره‌ی این خوشه تصمیم نگرفته — نه «منتشر کن»، نه '
    + '«رد کن»، نه «بازبینی». مرحله‌ی حکم برایش نچرخیده است.\n\n'
    + 'مثال: خوشه دیشب ساخته شد ولی اجرای شبانه به مرحله‌ی حکم نرسید. اجرای '
    + 'بعدی حکمش را می‌دهد.\n\n'
    + 'کارِ لازم: معمولاً هیچ — یک شب صبر. ولی اگر عددش بزرگ شد و بزرگ ماند، '
    + 'یعنی آن مرحله اصلاً اجرا نمی‌شود.',

  folded_into_group:
    'جدا منتشر نمی‌شود چون عضوِ یک سری است و والدش منتشر می‌شود. منتشر '
    + 'کردنش یعنی کاربر یک رویداد را دو بار ببیند.\n\n'
    + 'مثال: «جشنواره‌ی فیلم» ده روز طول می‌کشد. به‌جای ده رویدادِ جدا، یک '
    + 'رویداد با ده تاریخ در اپ ساخته می‌شود؛ آن نُه تای دیگر «در یک گروه تا '
    + 'شده»اند.\n\n'
    + 'نسبتش با «تا شده در سری»: همان اتفاق از دو طرف. آن یکی حالتِ رویداد '
    + 'است («سالم، زیرِ والدش هست») و این یکی دلیلی که publisher جدا منتشرش '
    + 'نمی‌کند.',
};

/** ستونِ عدد+برچسب. `warn` فقط وقتی روشن می‌شود که عدد واقعاً غیرِ صفر باشد. */
function Stat({ n, k, warn, help }: { n: number; k: string; warn?: boolean; help?: string }) {
  return (
    <div className={`card stat${warn && n > 0 ? ' warn' : ''}`}>
      <div className="n">{fmt(n)}</div>
      <div className="k"><Hint text={k} help={help} /></div>
    </div>
  );
}

function DetailTable({ rows, total, limit }: {
  rows: PublishSyncRow[]; total: number; limit: number;
}) {
  if (!rows.length) return <p className="dim">ردیفی نیست.</p>;
  return (
    <>
      <div className="scrollx">
        <table className="tbl">
          <thead>
            <tr>
              <th>حالت</th><th>دلیل</th>
              <th>عنوان — کرالر / اپ</th><th>شروع — کرالر / اپ</th>
              <th>وضعیتِ اپ</th><th>آخرین انتشار</th><th>شناسه</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const info = stateInfo(r.state);
              const titleDiff = r.app_title != null && r.title != null && r.app_title !== r.title;
              const startDiff = r.app_starts_at !== r.starts_at;
              return (
                <tr key={`${r.side}:${r.cluster_id ?? ''}:${r.app_event_id ?? ''}`}>
                  <td className={info.ok ? '' : 'bad'} style={{ whiteSpace: 'nowrap' }}>
                    <Hint text={info.label} help={info.help} />
                  </td>
                  <td className="dim">
                    {r.reason
                      ? <Hint text={REASONS[r.reason] ?? r.reason} help={REASON_HELP[r.reason]} />
                      : '—'}
                  </td>
                  <td className="ltr">
                    {r.title ?? <span className="dim">—</span>}
                    {/* طرفِ اپ فقط وقتی می‌آید که فرق داشته باشد: تکرارِ یک
                        رشته در هر ردیف، همان ردیفی را که واقعاً فرق دارد گم می‌کند. */}
                    {titleDiff ? <div className="dim">{r.app_title}</div> : null}
                  </td>
                  <td className="num">
                    {stamp(r.starts_at)}
                    {startDiff ? <div className="dim">{stamp(r.app_starts_at)}</div> : null}
                  </td>
                  <td className="dim">{r.app_status ?? '—'}</td>
                  <td className="dim num">{stamp(r.published_at)}</td>
                  <td className="mono dim">{r.external_id ?? r.cluster_id ?? '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {total > rows.length ? (
        <p className="note">
          {fmt(rows.length)} ردیف از {fmt(total)} نشان داده شده — سقفِ {fmt(limit)} ردیف.
          برای دیدنِ بقیه، یک حالت را از جدولِ بالا انتخاب کن.
        </p>
      ) : null}
    </>
  );
}

export function PublishSyncTab() {
  const qc = useQueryClient();
  // `null` یعنی «همه‌ی ناهماهنگ‌ها»؛ انتخابِ یک گروه هم state و هم reason را
  // می‌گذارد، چون ۱۶۴ یتیم سه دلیلِ کاملاً متفاوت دارند.
  const [pick, setPick] = useState<{ state: string; reason: string | null } | null>(null);
  const LIMIT = 200;

  const sync = useQuery({ queryKey: ['publishSync'], queryFn: opsRepo.publishSync });
  const detail = useQuery({
    queryKey: ['publishSyncDetail', pick?.state ?? null, pick?.reason ?? null],
    queryFn: () => opsRepo.publishSyncDetail(pick?.state, pick?.reason, LIMIT),
  });

  if (sync.isLoading) return <p className="dim">…</p>;
  if (sync.error) return <div className="card"><p className="err">{String(sync.error)}</p></div>;

  const d = sync.data!;
  if (d.error) {
    return (
      <div className="card">
        <h2>در دسترس نیست</h2>
        <p className="dim">
          {d.error === 'crawler-schema-missing'
            ? 'این دیتابیس اسکیمای crawler را ندارد — این صفحه فقط روی دیتابیسی معنی دارد که کرالر تویش می‌نویسد.'
            : 'اسکیمای crawler هست ولی ویوهای هماهنگی رویش ساخته نشده‌اند. مایگریشنِ ۰۰۳۲ی مخزنِ event_crawler روی این دیتابیس اجرا نشده.'}
        </p>
      </div>
    );
  }

  const s = d.summary!;
  const groups = d.groups ?? [];
  // RPC بر اساسِ تعداد مرتب می‌کند، پس دو دلیلِ یک حالت می‌توانند از هم جدا
  // بیفتند. این‌جا اول حالت‌ها کنارِ هم می‌آیند (به ترتیبِ جمعشان) و بعد
  // دلیل‌ها درونِ هر حالت — وگرنه نه توضیحِ یک‌بارِ حالت جا می‌افتد و نه
  // چشم می‌تواند سه دلیلِ یک حالت را با هم ببیند.
  const byState = new Map<string, number>();
  for (const g of groups) byState.set(g.state, (byState.get(g.state) ?? 0) + g.n);
  const ordered = [...groups].sort((a, b) =>
    (byState.get(b.state)! - byState.get(a.state)!)
    || a.state.localeCompare(b.state)
    || (b.n - a.n));
  const broken = ordered.filter((g) => !stateInfo(g.state).ok);
  const healthy = ordered.filter((g) => stateInfo(g.state).ok);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['publishSync'] });
    qc.invalidateQueries({ queryKey: ['publishSyncDetail'] });
  };

  // یک حالت می‌تواند چند دلیل داشته باشد (۱۶۴ یتیم، سه دلیل). توضیحش مالِ
  // خودِ حالت است، پس فقط روی اولین ردیفش می‌آید — تکرارِ همان جمله در سه
  // ردیفِ پشتِ‌سرِ هم، ستونِ «دلیل» را که واقعاً فرق دارد از چشم می‌اندازد.
  const groupRows = (list: PublishSyncGroup[]) => list.map((g, i) => {
    const info = stateInfo(g.state);
    const on = pick?.state === g.state && pick?.reason === g.reason;
    const firstOfState = i === 0 || list[i - 1].state !== g.state;
    return (
      <tr key={`${g.state}:${g.reason ?? ''}`}>
        <td>
          {/* دکمه خودش `title` می‌گیرد و نه `Hint`: زیرخطِ نقطه‌چین روی چیزی
              که از قبل زیرخطِ لینک دارد فقط شلوغی است، و دکمه هم خودش
              دعوت به ماوس بردن هست. */}
          <button type="button" className="linkbtn" title={info.help}
            onClick={() => setPick(on ? null : { state: g.state, reason: g.reason })}>
            {info.label}
          </button>
          {on ? (
            <span className="dim" style={{ marginRight: 6, fontSize: 11 }}>(انتخاب‌شده)</span>
          ) : null}
        </td>
        <td className="dim">
          {g.reason
            ? <Hint text={REASONS[g.reason] ?? g.reason} help={REASON_HELP[g.reason]} />
            : '—'}
        </td>
        <td className={`num${info.ok ? '' : ' bad'}`}>{fmt(g.n)}</td>
        <td className="dim">{firstOfState ? info.why : ''}</td>
      </tr>
    );
  });

  return (
    <>
      <div className="card">
        <div className="filters">
          <h2 style={{ margin: 0 }}>
            {s.in_sync_overall
              ? 'کرالر و اپ هماهنگ‌اند'
              : `${fmt(s.out_of_sync_total)} ردیف ناهماهنگ (${s.out_of_sync_pct}٪)`}
          </h2>
          <span className="spacer" style={{ flex: 1 }} />
          <button type="button" className="btn ghost" onClick={refresh}
            disabled={sync.isFetching || detail.isFetching}>
            {sync.isFetching || detail.isFetching ? '…' : 'تازه‌سازی'}
          </button>
        </div>
        {/* این جمله جای یک نمودار را می‌گیرد: بیشترِ «ناهماهنگی»ها فقط یعنی
            pipeline بعد از آخرین انتشار کار کرده. بدونِ این عدد، آدم دنبالِ
            خرابی می‌گردد جایی که فقط تأخیر است. */}
        <p className="note">
          {/* تاریخ درونِ جمله‌ی فارسی باید جزیره‌ی خودش باشد، وگرنه «p.m.»
              از انتهای تاریخ کنده می‌شود و وسطِ جمله می‌نشیند. */}
          آخرین انتشار از کرالر به اپ: <span className="ltr">{stamp(s.last_published_at)}</span>
          {' · '}{since(s.since_last_publish_seconds)} پیش
          {' · '}{fmt(s.published_last_24h)} ردیف در ۲۴ ساعتِ گذشته
        </p>
      </div>

      <h3 style={{ margin: '14px 0 6px' }}>سالم</h3>
      <div className="grid n4" style={{ marginBottom: 14 }}>
        <Stat n={s.ready_total} k="آماده‌ی انتشار" />
        <Stat n={s.in_sync} k="هماهنگ" />
        <Stat n={s.in_sync_as_occurrence} k="تا شده در سری"
          help={STATES.in_sync_as_occurrence.help} />
        <Stat n={s.archived_past + s.admin_decision} k="بایگانی / حکمِ ادمین" />
      </div>

      <h3 style={{ margin: '14px 0 6px' }}>ناهماهنگ</h3>
      <div className="grid n4" style={{ marginBottom: 14 }}>
        <Stat n={s.content_drift} k="محتوا عوض شده" warn />
        <Stat n={s.never_published} k="هرگز منتشر نشده" warn />
        {/* این یک کارت دو حالتِ متفاوت را جمع می‌زند، پس توضیحش هم باید هر
            دو را بگوید — وگرنه نصفِ عدد بی‌توضیح می‌ماند. */}
        <Stat n={s.app_row_missing + s.hidden_in_app} k="ردیفِ اپ گم/پنهان" warn
          help={'دو چیزِ متفاوت که هر دو یک نتیجه دارند: کاربر رویداد را '
            + 'نمی‌بیند.\n\n'
            + '«گم» یعنی ردیفی که منتشرش کرده بودیم دیگر در اپ نیست — مثلاً '
            + 'رویدادی که شناسه‌اش ۸۸۱ بود پاک شده و فقط یادداشتِ انتشارش '
            + 'مانده. این خرابی است.\n\n'
            + '«پنهان» یعنی ردیف هست ولی وضعیتش منتشر نیست — مثلاً در صفِ '
            + 'بازبینی مانده یا کسی ردش کرده. این معمولاً عمدی است.\n\n'
            + 'برای تفکیکشان، جدولِ «تفکیک» پایین را نگاه کن.'} />
        <Stat n={s.orphan_in_app} k="یتیم در اپ" warn help={STATES.orphan_in_app.help} />
      </div>

      {/* `dangling_map` جدا از بقیه است و عمداً در جمعِ «ناهماهنگ» نیست:
          نگاشتی است که ردیفِ اپش دیگر وجود ندارد، پس هیچ رویدادی پشتش نیست
          که بشود هماهنگ یا ناهماهنگ خواندش. زباله است، نه ناهماهنگی. */}
      {s.dangling_map > 0 ? (
        <p className="note" style={{ marginBottom: 14 }}>
          {fmt(s.dangling_map)}{' '}
          <Hint text="نگاشتِ آویزان"
            help={'یادداشتِ انتشاری که هیچ‌چیز پشتش نمانده: نه ردیفی در اپ، '
              + 'نه رویدادی در کرالر. زباله است، نه ناهماهنگی.\n\n'
              + 'مثال: مثلِ رسیدِ خریدی که هم کالا و هم فروشنده‌اش دیگر وجود '
              + 'ندارند. رسید مانده و به هیچ‌جا وصل نیست.\n\n'
              + 'فرقش با «ردیفِ اپ گم شده»: آن‌جا کرالر هنوز رویدادی پشتِ '
              + 'یادداشت دارد، پس واقعاً چیزی هست که باید در اپ باشد و نیست. '
              + 'این‌جا هیچ طرفی نمانده — برای همین در جمعِ «ناهماهنگ» بالا '
              + 'شمرده نشده.\n\n'
              + 'کارِ لازم: هیچ‌چیزِ فوری. تمیزکاری است.'} />
          {' '}در <span className="mono">published_events</span> —
          ردیفِ اپشان دیگر وجود ندارد. در جمعِ بالا نیامده‌اند چون رویدادی پشتشان نمانده.
        </p>
      ) : null}

      <div className="card">
        <h2>تفکیک</h2>
        <div className="scrollx">
          <table className="tbl">
            <thead>
              <tr><th>حالت</th><th>دلیل</th><th className="num">تعداد</th><th>یعنی چه</th></tr>
            </thead>
            <tbody>
              {groupRows(broken)}
              {groupRows(healthy)}
            </tbody>
          </table>
        </div>
        <p className="note">روی نامِ هر حالت بزن تا ردیف‌های واقعی‌اش پایین بیاید.</p>
      </div>

      <div className="card">
        <div className="filters">
          <h2 style={{ margin: 0 }}>
            {pick
              ? <>ردیف‌ها — <Hint text={stateInfo(pick.state).label}
                  help={stateInfo(pick.state).help} /></>
              : <Hint text="ردیف‌های ناهماهنگ"
                  help={'ردیف‌های واقعی، نه شمارش — این‌جا خودِ رویدادها را '
                    + 'می‌بینی.\n\n'
                    + 'تا وقتی از جدولِ «تفکیک» بالا چیزی انتخاب نکرده‌ای، '
                    + 'همه‌ی ناهماهنگ‌ها با هم می‌آیند (تا سقفِ ۲۰۰ ردیف). روی '
                    + 'نامِ یک حالت که بزنی، فقط همان می‌ماند و عنوانِ این کادر '
                    + 'هم به نامِ همان عوض می‌شود.\n\n'
                    + 'مثال: روی «یتیم در اپ» بزن تا فقط آن ۱۶۴ ردیف بیایند؛ '
                    + 'بعد «برداشتنِ فیلتر» برت می‌گرداند به همه.'} />}
          </h2>
          <span className="spacer" style={{ flex: 1 }} />
          {pick ? (
            <button type="button" className="btn ghost" onClick={() => setPick(null)}
              title="برگشت به فهرستِ همه‌ی ردیف‌های ناهماهنگ، بدونِ فیلترِ حالت.">
              برداشتنِ فیلتر
            </button>
          ) : null}
        </div>
        {detail.isLoading ? <p className="dim">…</p>
          : detail.error ? <p className="err">{String(detail.error)}</p>
          : detail.data?.error ? <p className="dim">در دسترس نیست.</p>
          : (
            <DetailTable rows={detail.data?.rows ?? []}
              total={detail.data?.total ?? 0} limit={LIMIT} />
          )}
      </div>
    </>
  );
}
